import { type CompanyUsersInfoList } from '@components/user-management-table/user-management-table.type';
import { useGenericInfiniteQuery } from '@hooks/use-generic-infinite-query';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { paramsSerializer } from '@utils/format-params';
import { Params } from '@utils/format-params/format-params.type';
import { request } from '@utils/request/request';
import { RequestError, RequestMethod } from '@utils/request/request.type';

import { QUERY_KEYS } from './constants.type';
import type { CompanyUsersListRequestParams, GetCompanyUserListHookParams } from './use-get-company-user-list.type';

export const GET_COMPANY_USERS_QUERY_KEY = QUERY_KEYS.getCompanyUserList;

const getCompanyUserList = async ({ companyId, params }: CompanyUsersListRequestParams) => {
  const queryParams = paramsSerializer(params as Params);
  const endpoint = `/v1/nightgraph/companies/${companyId}/users?${queryParams}`;
  const url = `${import.meta.env.VITE_USERS_HOST}${endpoint}`;
  return request({ url, method: RequestMethod.GET });
};

export const useGetCompanyUserList = ({
  companyId,
  options,
  params,
}: GetCompanyUserListHookParams): UseInfiniteQueryResult<InfiniteData<CompanyUsersInfoList, number>, RequestError> => {
  const { search, orderBy, sort, roles } = params;
  const queryParams = [GET_COMPANY_USERS_QUERY_KEY, companyId, search, orderBy, sort, roles];
  const infiniteQuery = useGenericInfiniteQuery<CompanyUsersInfoList, RequestError>(
    queryParams,
    ({ pageParam }) => {
      let offset = 0;
      const initialLimit = params.limit ?? 10;
      if (pageParam !== 0) {
        offset = pageParam === 1 ? initialLimit : (pageParam - 1) * 10 + initialLimit;
      }
      return getCompanyUserList({ companyId, params: { ...params, offset: offset || 0 } });
    },
    {
      ...options,
      initialPageParam: 0,
      getNextPageParam: (lastPage: CompanyUsersInfoList, allPages: CompanyUsersInfoList[]) => {
        if (
          (allPages.length > 1 && lastPage.length < 10) ||
          (allPages.length === 1 && lastPage.length < (params.limit || 10))
        ) {
          return undefined;
        }
        return allPages.length;
      },
    }
  );
  return infiniteQuery;
};
